import ApiService from '@/shared/services/api.service';

const PAYMENT_REQUEST = Object.freeze({ suppressExpiredTokenModal: true });

export class ApplicationsService {
    static list(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/applications', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static listAll(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/applications/listAll', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static listStaff(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/staff-applications', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static find(id) {
        return new Promise((resolve, reject) => {
            // A 403/404 here means the application is missing or not the user's; we
            // redirect to the 404 page, so suppress the generic session-expired modal.
            ApiService.get(`/applications/${id}`, undefined, { suppressExpiredTokenModal: true })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static findStaff(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}`, undefined, { suppressExpiredTokenModal: true })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static create(data) {
        return new Promise((resolve, reject) => {
            ApiService.post('/applications?echo=true', { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static registerStaffApplication(data) {
        return new Promise((resolve, reject) => {
            ApiService.post('/staff-applications/register?echo=true', { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static updateRegisteredStaffApplication(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/register`, { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }
    static generateRegistryDoc(data) {
        return new Promise((resolve, reject) => {
            ApiService.post('/staff-applications/generate-registry-doc?echo=true', { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static updateGeneratedRegistryDoc(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/generate-registry-doc`, { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static updateRegistryApplication(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/registry-application`, { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static deleteRegistryApplication(id) {
        return new Promise((resolve, reject) => {
            ApiService.delete(`/staff-applications/${id}/registry-application`, { })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }    

    static updateRegistryApplicationForm(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/registry-application-form`, { ...data })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static sendRegistry(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/registry-send`, { ...data })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static registerPayment(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/register-payment`, { ...data })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static checkRegistryApplicationPaymentStatus(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}/registry-check-payment-status`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static getRelatedDocuments(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/staff-applications', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static update(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/applications/${id}`, { ...data })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static updateCitizenClarifications(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/applications/${id}/clarifications`, { ...data })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }    

    static updateStaff(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}`, { ...data })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static appendFiles(id, files) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/add-files`, { files })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static findFiles(id, token, params) {
        return new Promise((resolve, reject) => {
            if (token) {
                ApiService.query(`/files/${id}`, {
                    params, headers: {
                        Accept: 'application/json',
                        'x-file-access-token': token,
                    }
                })
                    .then((response) => resolve(response.data))
                    .catch((error) => reject(error));
            } else {
                resolve([]);
            }
        });
    }

    static assignStaff(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/assign`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static assignReviewer(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/assign-reviewer`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static updateResponseDueInDays(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/response-due-in-days`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static approveDocument(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/approve`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static approveClarificationDocument(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/approve`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static modifyApplicationType(applicationId, applicationTypeId, applicationTypeName) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/modify-application-type`, { application_type_id: applicationTypeId, application_type_name: applicationTypeName })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static rejectDocument(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/reject`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static rejectClarificationDocument(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/reject`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static startClarification(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/start-clarification`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static rejectViza(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/viza-reject`, {})
                .then((response) => resolve(response.data))
                .catch((error) => {
                    // Normalize error message for translation lookup
                    if (error?.response?.data?.message) {
                        const errorMessage = Array.isArray(error.response.data.message) 
                            ? error.response.data.message[0]
                            : error.response.data.message;
                        
                        // Convert error message to match translation key format (replace spaces with underscores)
                        error.response.data.message = errorMessage.replace(/\s+/g, '_');
                    }
                    reject(error);
                });
        });
    }

    static sendDocumentToStaffReview(id) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/staff-review`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static documentReviewedByStaff(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/application-reviewed-by-staff-function`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static signDocument(id, action, customImageId, stsSigning = false) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${action}/sign`, { customImageId, stsSigning })
                .then((response) => {
                    // TODO: Remove after BE imp
                    if (response.data.success) {
                        resolve(response.data);
                    } else {
                        reject(response.data);
                    }
                })
                .catch((error) => reject(error));
        });
    }

    static signClarificationDocument(id, action, customImageId, clarificationId, stsSigning = false) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/sign-clarification/${action}`, { customImageId, stsSigning })
                .then((response) => {
                    // TODO: Remove after BE imp
                    if (response.data.success) {
                        resolve(response.data);
                    } else {
                        reject(response.data);
                    }
                })
                .catch((error) => reject(error));
        });
    }    

    static signDocumentCitizen(id, review) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/sign${review ? '?review=true' : ''}`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static signClarificationDocumentCitizen(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/${encodeURIComponent(clarificationId)}/sign-clarification`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static signDocumentApplicantUser(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/sign-output-document`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static validateDocumentSign(payload) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${payload.applicationId}/confirm-signing`, {
                pin: payload.pin,
                stsSigning: payload.stsSigning || false
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static validateClarificationDocumentSign(payload) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${payload.applicationId}/${encodeURIComponent(payload.clarificationId)}/confirm-clarification-signing`, {
                pin: payload.pin,
                stsSigning: payload.stsSigning || false
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static validateClarificationDocumentSignCitizen(payload) {
        return new Promise((resolve, reject) => {
            const { pin, review } = payload;
            const body = {
                pin,
            };
            if (review) {
                body.review = review;
            }
            ApiService.put(`/applications/${payload.applicationId}/${encodeURIComponent(payload.clarificationId)}/confirm-clarification-signing`, body)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static validateDocumentSignCitizen(payload) {
        return new Promise((resolve, reject) => {
            const { pin, review } = payload;
            const body = {
                pin,
            };
            if (review) {
                body.review = review;
            }
            ApiService.put(`/applications/${payload.applicationId}/confirm-signing`, body)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static validateDocumentSignApplicantUser(payload) {
        return new Promise((resolve, reject) => {
            const { pin } = payload;
            const body = {
                pin,
            };
            ApiService.put(`/applications/${payload.applicationId}/confirm-signing-output-document`, body)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static checkSigningStatus(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/${id}/check-signing-status`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static checkClarificationSigningStatus(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/check-clarification-signing-status`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static checkStaffSigningStatus(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}/check-signing-status`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static downloadDocument(token, id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/files/${id}`, {
                'x-file-access-token': token,
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static calculateTaxes(id, data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/application-types/${id}/all-taxes`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static calculateTaxesForApplicationType(data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/application-types/${data.id}/taxes`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static finalizeApplication(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/send`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static findUserActions(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/user-actions', { params })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static previewDocument(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}/preview-document`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static previewClarificationDocument(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/preview-clarification-document`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }
    
    static previewCitizenClarificationDocument(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/${id}/${encodeURIComponent(clarificationId)}/preview-clarification-document`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }
    
    static generateCitizenClarificationDocument(id, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/${encodeURIComponent(clarificationId)}/generate-clarification-document`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }      

    static previewDocumentByCitizen(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/${id}/preview-document`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static generateFinalDocument(id, action) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${action}/generate-document`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static generateFinalClarificationDocument(id, action, clarificationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/${encodeURIComponent(clarificationId)}/generate-clarification-document/${action}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static upload(token, files) {
        return new Promise((resolve, reject) => {
            ApiService.post('/files?echo=true', files, {
                'content-type': 'multipart/form-data',
                'x-file-access-token': token,
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static qrUpload(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post('/qr/upload-files', payload, {
                'content-type': 'multipart/form-data'
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static pay({ id, data, payment_initiated }) {
        const url = payment_initiated ? `applications/${id}/refresh-payment-url` : `/applications/${id}/pay`;
        return new Promise((resolve, reject) => {
            ApiService.post(url, data)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static initializeOfflineSigning(id, filesLocation) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/applications/${id}/initialize-offline-sign?filesLocations=${filesLocation}`)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static checkOfflineSigning(id, new_pdf_id) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/applications/${id}/check-offline-sign`, { new_pdf_id })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static finalizeOfflineSigning(id, newPdfId) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/applications/${id}/finish-offline-sign/${newPdfId}`)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static enrollment(data) {
        return new Promise((resolve, reject) => {
            ApiService.post('/enrollment/person', { ...data })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static paymentCallback(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/payments/callback', params)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static paymentsList(params, isStaff = true) {
        const endpoint = isStaff ? '/payments/all' : '/payments/owned';
        return new Promise((resolve, reject) => {
            ApiService.query(endpoint, params)
                .then((response) => {
                    resolve({ ...response.data, fileToken: response.headers['x-file-download-access-token'] });
                })
                .catch((error) => reject(error));
        });
    }

    static getSystemMetadata() {
        return new Promise((resolve, reject) => {
            ApiService.get('/applications/system-metadata')
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static getActivities(id) {
        return new Promise((resolve, reject) => {
            ApiService.query(`user-messages/identification/${id}`, {
                params: {
                    sort: '-created_date',
                    limit: 100  // TODO: at some point 100 will not be enough
                }
            })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static sendStaffComment(params) {
        return new Promise((resolve, reject) => {
            ApiService.post(`staff-applications/${params.id}/comment`, { ...params.payload })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }
    static sendComment(params) {
        return new Promise((resolve, reject) => {
            ApiService.post(`applications/${params.id}/comment`, { ...params.payload })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static addComments(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/${applicationId}/comment`, payload)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => reject(error));
        });
    }

    static returnDocument(params) {
        const { applicationId, payload } = params;
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${applicationId}/return-document`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static findRelatedFiles(id) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/staff-applications/${id}/related-files`)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        })
    }

    static sendReview(id, citizen_review) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/applications/${id}/send-review`, { citizen_review })
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        })
    }

    static startCitizenClarification(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/applications/${id}/start-clarification`, {})
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        })
    }

    static checkGenerationStatus(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/${id}/check-generation-status`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static signDocumentBulk(application_ids, custom_image_id, stsSigning = false) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/bulk-sign`, { application_ids, custom_image_id, stsSigning })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static signClarificationDocumentBulk(application_ids, custom_image_id, stsSigning = false) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/clarification-bulk-sign`, { application_ids, custom_image_id, stsSigning })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }    

    static checkStaffBulkSigningStatus(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/check-bulk-signing-status`, { application_ids: payload })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static checkClarificationBulkSigningStatus(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/check-clarification-bulk-signing-status`, { application_ids: payload })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static validateBulkDocumentSign(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/confirm-bulk-signing`, {
                pin: payload.pin,
                request_id: payload.applicationId,
                stsSigning: payload.stsSigning || false
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static validateClarificationBulkDocumentSign(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/confirm-clarification-bulk-signing`, {
                pin: payload.pin,
                request_id: payload.applicationId,
                stsSigning: payload.stsSigning || false
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }    

    static findUrbanismRegulations(uat_ids) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/urbanism-regulation-zones/groups?uat_ids=${uat_ids.join(',')}`)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        })
    }

    static findUrbanismRegulationEntriesByZone(zone_id) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/urbanism-regulations`, {
                params: {
                    query: `zone_id eq ${zone_id}`
                }
            })
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        })
    }

    static manualCompletion(id, data, forceUpdate) {
        return new Promise((resolve, reject) => {
            const queryParam = forceUpdate ? `?force=true` : '';
            ApiService.patch(`/staff-applications/${id}/manual-completion${queryParam}`, { ...data })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static findStaffByDocumentSeries(args) {
        return new Promise((resolve, reject) => {
            let queryParams = `value=${args.value}`
            if (args.getFileToken) {
                queryParams += `&getFileToken=true`
            }
            if (args.parentId) {
                queryParams += `&parentAppId=${args.parentId}`
            }
            ApiService.get(`/staff-applications/search?${queryParams}`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static findByDocumentSeries(series) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/find?series=${series}`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static findByDocumentSeriesPublic(series) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/applications/findAll?series=${series}`)
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static findBeneficiary(id) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/beneficiaries/${id}`)
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        });
    }

    static findBeneficiaries(params) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/beneficiaries`, params)
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => reject(error));
        });
    }

    static addBeneficiary(data) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/beneficiaries?echo=true`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static deleteApplication(id) {
        return new Promise((resolve, reject) => {
            ApiService.delete(`/applications/${id}/draft`)
                .then(() => resolve())
                .catch((error) => reject(error));
        });
    }

    static removeApplicationDoc(payload) {
        const { file_id, app_id } = payload;
        return new Promise((resolve, reject) => {
            ApiService.patch(`staff-applications/${app_id}/remove-document`, { file_id })
                .then((data) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static signAttachmentsBulk({ application_id, file_ids, custom_image_id, stsSigning = false }) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/attachments-bulk-sign`, { application_id, file_ids, custom_image_id, stsSigning })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static downloadCompression({ application_id, files }) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${application_id}/download-compression`, { files })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static downloadCompressionAsCitizen({ application_id, files }) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/applications/${application_id}/download-compression`, { files })
                .then((response) => resolve(response))
                .catch((error) => reject(error));
        });
    }

    static validateBulkAttachmentsSign(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/confirm-attachments-bulk-signing`, {
                pin: payload.pin,
                application_id: payload.applicationId,
                request_id: payload.file_ids,
                stsSigning: payload.stsSigning || false
            })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static getAssignedStaffAccount(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`users/${id}`)
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static checkApplicationPaymentStatus(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`payments/application/${id}/bt-url-check`)
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static getLayers(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/layers/me', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static getPublicLayers(params) {
        return new Promise((resolve, reject) => {
            ApiService.query('/public-layers/', params)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static closeApplication(id) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/close-application`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static createPaymentLink(id, amount, email) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/create-payment-link`, { amount, email })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    // The payment calls below say themselves what a 403 means (a refusal, a missing permission
    // or an ended session), so the generic session-expired modal is kept out of their way.

    static recordManualPayment(applicationId, paymentId, data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${applicationId}/payments/${paymentId}/record`, data, PAYMENT_REQUEST)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static addManualPayment(applicationId, data) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${applicationId}/payments/add`, data, PAYMENT_REQUEST)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static checkPaymentWithBank(applicationId, paymentId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${applicationId}/payments/${paymentId}/check`, {}, PAYMENT_REQUEST)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    // brings the application in line with its payments, without asking the bank
    static syncPayments(applicationId) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${applicationId}/payments/sync`, {}, PAYMENT_REQUEST)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static paymentJournal(paymentId) {
        return new Promise((resolve, reject) => {
            ApiService.query('/payments/journal', { params: { payment_id: paymentId }, ...PAYMENT_REQUEST })
                .then((response) => {
                    resolve({ lines: response.data?.data ?? [], fileToken: response.headers['x-file-download-access-token'] });
                })
                .catch((error) => reject(error));
        })
    }

    // the upload token of an application comes with the application itself;
    // a refusal here is a session that ended, which the general dialog announces
    static staffUploadToken(applicationId) {
        return new Promise((resolve, reject) => {
            ApiService.query(`/staff-applications/${applicationId}`, { params: { fields: 'id' } })
                .then((response) => resolve(response.headers['x-file-upload-access-token']))
                .catch((error) => reject(error));
        })
    }

    static startProcess(id) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/staff-applications/${id}/start-process`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }

    static startSubProcess(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/start-subprocess`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    }    

    static getApplicationTypesByOrganisation(domain) {
        return new Promise((resolve, reject) => {
            let query = '/application-types/organisation';
            if (domain) {
                query += `?domain=${domain}`
            }
            ApiService.query(query, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static getApplicationTypesGrouped(domain) {
        return new Promise((resolve, reject) => {
            let query = '/application-types/grouped';
            if (domain) {
                query += `?domain=${domain}`
            }
            ApiService.query(query, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static sealAttachmentsBulk(id) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/staff-applications/attachments-bulk-sealing`, { id })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error));
        });
    }

    static validateGisFile({ application_id, file_id }) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/gis-files`, { application_id, file_id })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static getPredefinedReports() {
        return new Promise((resolve, reject) => {
            ApiService.query('/predefined-reports', {
                    params: {
                        limit: 100
                    }
                })
                .then((response) => {
                    resolve(response.data.data)
                })
                .catch((error) => reject(error));
        });
    }

    static savePredefinedReport(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/predefined-reports`, payload)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static updatePredefinedReport(payload, id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/predefined-reports/${id}`, payload)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        });
    }

    static generateApplicationsCsv(payload) {
        return new Promise((resolve, reject) => {
            // `config` is the fourth argument; passing it third makes it the headers object and
            // leaves responseType at 'json'. The response would then be read as text, which drops
            // the UTF-8 BOM and leaves Excel showing mojibake for the Romanian diacritics.
            ApiService.post(`/reported-applications/generate/csv`, payload, {}, { responseType: 'blob' })
                .then((response) => {
                    resolve(response);
                })
                .catch(async (error) => {
                    // responseType 'blob' also applies to error bodies, so the JSON message
                    // (e.g. the date range limit) has to be decoded before anyone can read it.
                    const body = error?.response?.data;
                    if (body instanceof Blob) {
                        try {
                            error.response.data = JSON.parse(await body.text());
                        } catch (e) {
                            // not JSON, keep the blob
                        }
                    }
                    reject(error);
                });
        });
    }

    static inviteBeneficiary(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post('/notifications/invite-beneficiary', payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static generateQR(payload) {
        return new Promise((resolve, reject) => {
            ApiService.post('/qr/generate', payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static findBeneficiariesInAnaf(data) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/beneficiaries/interogate-anaf?echo=true`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static getSignatureCustomImageToken() {
        return new Promise((resolve, reject) => {
            ApiService.get(`/staff-applications/signature-custom-image-token`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static generateStaffReviewDocument(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/staff-review-document`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    static sendToClarification(id) {
        return new Promise((resolve, reject) => {
            ApiService.put(`/staff-applications/${id}/send-to-clarification`, {})
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }
}
